import type { ImageCapture } from "@rumble-it/common-lib/types/models/ImageCapture.interface";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button, Card, Input, Select } from "@rumbledirect/ui";
import { Form, message } from "antd";
import { useMemo } from "react";

interface ImageCaptureFormCardProps {
  mutation: UseMutationResult<null, Error, ImageCapture, unknown>;
  loading?: boolean;
  initialValues?: ImageCapture;
  title?: string;
  buttonText?: string;
  accountOptions?: { label: string; value: string }[];
}

export const ImageCaptureFormCard = ({
  mutation,
  loading,
  initialValues,
  title = "ImageCapture aanmaken",
  buttonText = "Aanmaken",
  accountOptions = [],
}: ImageCaptureFormCardProps) => {
  const initialFormValues = useMemo(() => {
    if (!initialValues) return undefined;

    return {
      ...initialValues,
      settings:
        typeof initialValues.settings === "string"
          ? initialValues.settings
          : JSON.stringify(initialValues.settings ?? {}, null, 2),
      account: (initialValues.account as any)?.["@id"],
    };
  }, [initialValues]);

  const handleFinish = async (
    values: Partial<ImageCapture> & { settings?: string },
  ) => {
    // Parse settings to object if it's a string
    let parsedSettings: unknown = values.settings;
    if (typeof values.settings === "string" && values.settings.trim() !== "") {
      try {
        parsedSettings = JSON.parse(values.settings as string);
      } catch {
        message.error("Instellingen moeten geldige JSON zijn");
        return;
      }
    }

    const payload: ImageCapture = {
      ...initialValues,
      ...values,
      settings: parsedSettings as ImageCapture["settings"],
    } as ImageCapture;

    mutation.mutate(payload);
  };

  return (
    <Card loading={loading} title={title}>
      <Form
        onFinish={handleFinish}
        layout="vertical"
        initialValues={initialFormValues}
      >
        <Form.Item
          name="title"
          label="Naam"
          rules={[{ required: true, message: "Naam is verplicht" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="account"
          label="ImageCapture account"
          rules={[{ required: true, message: "Selecteer een account" }]}
        >
          <Select
            placeholder="Selecteer een account"
            options={accountOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="settings"
          label="Instellingen"
          rules={[{ required: true, message: "Instellingen zijn verplicht" }]}
        >
          <Input.TextArea rows={10} />
        </Form.Item>

        <Form.Item label={null}>
          <Button loading={mutation.isPending} type="primary" htmlType="submit">
            {buttonText}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
